<template>
  <SogPage title="Bordereau-Perioden">
    <SogCard>
      <SogCardActions :is-toolbar="true">
        <SogDropDownList
          v-model="_bordereauId"
          style="width: 140px"
          name="PartnerShipID"
          :data-source="_items"
        />
        <SogCardActionButton
          :kind="SogButtonKind.Search"
          @click="onRefresh"
        />
        <SogCardActionButton
          v-show="_isPeriodClosVisible"
          tooltip="Periode schließen"
          mdi-icon="mdi-lock"
          @click="onLock"
        />
        <SogCardActionButton
          v-show="_isWakam && _selectedPeriod && _selectedPeriod.closedDate"
          tooltip="Periode öffnen"
          mdi-icon="mdi-lock-open-variant"
          @click="onUnlock"
        />
        <SogCardActionButton
          v-show="_selectedPeriod"
          tooltip="Excel-Export"
          mdi-icon="mdi-file-excel"
          @click="onExport(false,false)"
        />
        <SogCardActionButton
          v-show="_isCsvExportVisible"
          tooltip="Csv-Export"
          mdi-icon="mdi-file-delimited"
          @click="onExport(true,false)"
        />
        <SogCardActionButton
          v-show="_isUploadVisible"
          tooltip="Upload an Wakam"
          mdi-icon="mdi-upload"
          @click="onExport(true,true)"
        />
        <SogCardActionButton
          :kind="SogButtonKind.Back"
        />
      </SogCardActions>
      <SogCardContent>
        <SogProgressInfoComponent
          :progress-info="_progressInfo"
          :show-caption="false"
        />
      </SogCardContent>
      <SogCardContent>
        <BordereauListComponent
          ref="_listComponent"
          :bordereau-id="_bordereauId"
          @selected="onSelected"
        />
      </SogCardContent>
    </SogCard>
  </SogPage>
</template>

<script setup lang="ts">

import SogPage from "@/shared/ui/SogPage.vue";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";
import SogCardContent from "@/shared/ui/SogCardContent.vue";


import notificationService from "@/shared/services/NotificationService";
import BordereauListComponent from "@/modules/WakamBordereau/components/BordereauListComponent.vue";
import {computed, onMounted, reactive, ref} from "vue";
import {RouteLocationNormalizedLoaded, useRoute} from "vue-router";
import progressService from "@/shared/services/ProgressService";
import {ProgressInfo} from "@/shared/model/ProgressInfo";
import apiFile from "@/shared/services/apiFile";
import SogGrid from "@/shared/ui/SogGrid.vue";
import apiBordereau from "@/modules/WakamBordereau/api";
import SogDropDownList from "@/shared/ui/SogDropDownList.vue";
import BordereauPeriod from "@/shared/model/BordereauPeriod";
import SogProgressInfoComponent from "@/shared/ui/SogProgressInfoComponent.vue";

const _id = ref("");
const _listComponent = ref<InstanceType<typeof SogGrid> | null>(null)
const _bordereauId = ref("bordereau_wakam")
const _items = reactive([{id: "bordereau_wakam", value: "Short-MTPL"},
  {
  id: "bordereau_wakam_us_nato",
  value: "US-NATO"
  },
  {
    id: "hiscox",
    value: "Lokky/Hiscox (temporel)"
  },
]);
const _selectedPeriod = ref<BordereauPeriod | undefined>(undefined)
const _params = reactive({ftp: false, periodId: ""})

function p_init(route: RouteLocationNormalizedLoaded) {
  _id.value = route.params.id as string;
}

const _progressInfo = ref<ProgressInfo | undefined>(undefined)

async function onExport(csv: boolean, ftp: boolean) {
  if (!_selectedPeriod.value)
    return
  _params.ftp = ftp
  _params.periodId = _selectedPeriod.value.id ?? "";
  const partnerships = _bordereauId.value === "bordereau_wakam" ? ['178030101', '178030104'] : _bordereauId.value === "bordereau_wakam_us_nato"? ['178030103']:[_bordereauId.value];
  const id = await apiBordereau.initExport(_selectedPeriod.value.id ?? "", partnerships, _bordereauId.value === "bordereau_wakam", csv, ftp)
  if (id) {
    await progressService.addToGroup([id], false, onProgressRefresh)
  }
}


onMounted(async () => {
  const route = useRoute();
  await p_init(route)
  //console.log("id",_currenId.value);
})

async function onLock() {
  if (!_selectedPeriod.value)
    return
  const id = await apiBordereau.lock(_selectedPeriod.value.id)
  if (id) {
    notificationService.success("Periode wurde geschlossen")

  }
  _listComponent.value?.refresh()
}

async function onUnlock() {
  notificationService.showYesNo("Periode Öffnen", "Möchten Sie wirklich die Periode öffnen", async () => {
    if (!_selectedPeriod.value)
      return
    const id = await apiBordereau.unlock(_selectedPeriod.value.id)
    if (id) {
      notificationService.success("Periode wurde geöffnet")
    }
    _listComponent.value?.refresh()
  })
}

async function onProgressRefresh(group: string, progressInfo: ProgressInfo) {
  _progressInfo.value = progressInfo
  if (progressInfo) {
    if (progressInfo.finishDate) {
      await progressService.removeFromGroup([progressInfo.groupId ?? ""], onProgressRefresh)
      if (!progressInfo.successful) {
        notificationService.error(progressInfo.resultMessage ?? "")
      } else {
        if (progressInfo.resultId) {
          _listComponent.value?.refresh()
          await apiFile.downloadFile(progressInfo.resultId)
          if (_params.ftp) {
            await apiBordereau.sendMail(_params.periodId, progressInfo.resultId)
          }
        }
      }
      _progressInfo.value = undefined
    }
  }
}

function onSelected(period?: BordereauPeriod) {
  _selectedPeriod.value = period
}

function onRefresh() {
  _listComponent.value?.refresh()
}

const _isWakam = computed(()=>{
  return (_bordereauId.value === "bordereau_wakam_us_nato"||_bordereauId.value === "bordereau_wakam")
})

const _isCsvExportVisible = computed(()=>{
  return _selectedPeriod.value && _isWakam.value
})

const _isUploadVisible = computed(()=>{
  return _isWakam.value&&_selectedPeriod.value && _selectedPeriod.value.closedDate
})

const _isPeriodClosVisible = computed(()=>{
  return _isWakam.value&&_selectedPeriod.value&&!_selectedPeriod.value.closedDate
})


</script>

<style scoped>

</style>
